// NeedView — the ONE shared way to display a job posting (annons).
//
// Employer detail, candidate detail, and Link's artifact preview all render
// through this component. It takes a normalized NeedViewData (adapters below
// map from the employer `Need`, the candidate `PublicNeed`, or a parsed Link
// artifact) plus mode-agnostic slots: `statusBadge` + `actions` (header),
// `children` (extra sections like course gating / onboarding), `footer`
// (e.g. the candidate Apply bar). Same data, same formatters, same layout —
// kills the old 4-way divergence.

import React from 'react';
import ReactMarkdown from 'react-markdown';
import {
    Briefcase, MapPin, Clock, Calendar, Building2, Users, Coins, CheckCircle2,
    Mail, Phone, User, CalendarClock,
} from 'lucide-react';
import { EntityAvatar } from '@/components/custom-ui/EntityAvatar';
import type { Need, NeedContact, EmploymentType, EmploymentExtent, SalaryType } from '@/lib/models/need';
import {
    formatHoursRange, formatWorkLocation, formatEmployment, formatSalary, formatShortDate,
} from '@/lib/models/need';
import type { PublicNeed } from '@/lib/models/jobApplication';

export interface NeedViewData {
    role: string;
    unitLabel?: string;        // the unit name (employer Need.title / PublicNeed.unit_name)
    organisationName?: string;
    organisationLogoUrl?: string; // #40; absent → name-derived placeholder
    description?: string;
    bodyMarkdown?: string;     // rich multi-section source-of-truth body
    competencies: string[];
    city: string;
    minHours: number;
    maxHours: number;
    workLocationOnSite: boolean;
    workLocationRemote: boolean;
    offersSupervision: boolean;
    startDate: string;
    applicationDeadline?: string;
    employmentType?: EmploymentType;
    employmentExtent?: EmploymentExtent;
    extentPercent?: number;
    positionsCount?: number;
    contacts: NeedContact[];
    salaryMin?: number;
    salaryMax?: number;
    salaryType?: SalaryType;
    createdAt?: string;
    updatedAt?: string;
}

// ── Adapters ────────────────────────────────────────────────────────
// The employer `Need` only carries organisation_id, so the org's name/logo are
// passed in (from useEmployer's employerDetails.organisation). This keeps the org
// placeholder identical to what the candidate side derives from organisation_name
// — same org → same color + initials everywhere (#40).
export const needToView = (n: Need, org?: { name?: string; logo_url?: string }): NeedViewData => ({
    role: n.role,
    unitLabel: n.title,
    organisationName: org?.name,
    organisationLogoUrl: org?.logo_url,
    description: n.description,
    bodyMarkdown: n.body_markdown,
    competencies: n.competencies ?? [],
    city: n.city,
    minHours: n.min_hours_per_week,
    maxHours: n.max_hours_per_week,
    workLocationOnSite: n.work_location_on_site,
    workLocationRemote: n.work_location_remote,
    offersSupervision: n.offers_supervision,
    startDate: n.start_date,
    applicationDeadline: n.application_deadline,
    employmentType: n.employment_type,
    employmentExtent: n.employment_extent,
    extentPercent: n.extent_percent,
    positionsCount: n.positions_count,
    contacts: n.contacts ?? [],
    salaryMin: n.salary_min,
    salaryMax: n.salary_max,
    salaryType: n.salary_type,
    createdAt: n.created_at,
    updatedAt: n.updated_at,
});

export const publicNeedToView = (p: PublicNeed): NeedViewData => ({
    role: p.role,
    unitLabel: p.unit_name,
    organisationName: p.organisation_name,
    organisationLogoUrl: p.organisation_logo_url,
    description: p.description,
    bodyMarkdown: p.body_markdown,
    competencies: p.competencies ?? [],
    city: p.city,
    minHours: p.min_hours_per_week,
    maxHours: p.max_hours_per_week,
    workLocationOnSite: p.work_location_on_site,
    workLocationRemote: p.work_location_remote,
    offersSupervision: p.offers_supervision,
    startDate: p.start_date,
    applicationDeadline: p.application_deadline,
    employmentType: p.employment_type,
    employmentExtent: p.employment_extent,
    extentPercent: p.extent_percent,
    positionsCount: p.positions_count,
    contacts: p.contacts ?? [],
    salaryMin: p.salary_min,
    salaryMax: p.salary_max,
    salaryType: p.salary_type,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
});

// "Senast uppdaterad" is only meaningful once a posting has actually been
// edited after creation — a brand-new posting has updated_at ≈ created_at, so
// we suppress the line there (>60s gap counts as a real edit; publishing a
// draft also bumps updated_at, which is a legitimate change to surface).
const editedDate = (createdAt?: string, updatedAt?: string): string | null => {
    if (!updatedAt) return null;
    const u = new Date(updatedAt).getTime();
    if (isNaN(u)) return null;
    const c = createdAt ? new Date(createdAt).getTime() : NaN;
    if (!isNaN(c) && u - c <= 60_000) return null;
    return formatShortDate(updatedAt);
};

// ── Stat tile ───────────────────────────────────────────────────────
const Tile: React.FC<{ icon: React.ComponentType<any>; label: string; value: string }> = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-gray-50 border border-gray-100">
        <Icon className="w-4 h-4 text-[#1A4D4F] mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
            <p className="text-sm font-semibold text-gray-900 truncate">{value}</p>
        </div>
    </div>
);

interface NeedViewProps {
    data: NeedViewData;
    statusBadge?: React.ReactNode;   // employer status / candidate applied-status
    actions?: React.ReactNode;       // header-right buttons (mode-specific)
    children?: React.ReactNode;      // extra sections (course gating, onboarding, ...)
    footer?: React.ReactNode;        // sticky bottom (e.g. candidate Apply)
    // 'org' (default) shows the org logo/placeholder; 'icon' keeps the generic
    // briefcase glyph — used by the Link artifact preview, where there's no real
    // org identity yet (#40).
    headerVariant?: 'org' | 'icon';
}

const NeedView: React.FC<NeedViewProps> = ({ data, statusBadge, actions, children, footer, headerVariant = 'org' }) => {
    const subtitle = [data.unitLabel, data.organisationName].filter(Boolean).join(' · ');
    const lastEdited = editedDate(data.createdAt, data.updatedAt);

    return (
        <div className="space-y-5">
            {/* Header — stacks on mobile so the action buttons never overflow the title */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="flex items-start gap-3 min-w-0">
                    {/* 'icon' = employer's own posting (no logo — always their org);
                        'org' = candidate view / candidate-preview (icon + org logo badge). */}
                    <EntityAvatar
                        icon={Briefcase}
                        size="xl"
                        orgName={headerVariant === 'icon' ? undefined : (data.organisationName || data.unitLabel)}
                        orgLogoUrl={headerVariant === 'icon' ? undefined : data.organisationLogoUrl}
                    />
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-xl font-bold text-gray-900">{data.role || 'Namnlös roll'}</h1>
                            {statusBadge}
                        </div>
                        {subtitle && (
                            <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                                <Building2 className="w-3.5 h-3.5" /> {subtitle}
                            </p>
                        )}
                        {lastEdited && (
                            <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-1">
                                <CalendarClock className="w-3 h-3" /> Senast uppdaterad {lastEdited}
                            </p>
                        )}
                    </div>
                </div>
                {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
            </div>

            {/* Stat tiles */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                <Tile icon={MapPin} label="Ort" value={data.city || '—'} />
                <Tile icon={Clock} label="Omfattning" value={formatHoursRange(data.minHours, data.maxHours)} />
                <Tile icon={Briefcase} label="Arbetsform" value={formatWorkLocation(data.workLocationOnSite, data.workLocationRemote)} />
                <Tile icon={Calendar} label="Startdatum" value={formatShortDate(data.startDate) || '—'} />
                {data.employmentType && (
                    <Tile icon={User} label="Anställning" value={formatEmployment(data.employmentType, data.employmentExtent, data.extentPercent)} />
                )}
                <Tile icon={Coins} label="Lön" value={formatSalary(data.salaryType, data.salaryMin, data.salaryMax)} />
                {data.applicationDeadline && (
                    <Tile icon={CalendarClock} label="Sista ansökningsdag" value={formatShortDate(data.applicationDeadline)} />
                )}
                {!!data.positionsCount && data.positionsCount > 1 && (
                    <Tile icon={Users} label="Antal tjänster" value={String(data.positionsCount)} />
                )}
            </div>

            {/* Supervision banner */}
            {data.offersSupervision && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-800">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Handledning erbjuds
                </div>
            )}

            {/* Body — rich markdown source-of-truth, else flat description */}
            {data.bodyMarkdown ? (
                <div className="prose prose-sm max-w-none text-gray-700 [&_h1]:hidden [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mt-5 [&_h2]:mb-2">
                    {/* hide the H1 (role already in header) + the HTML meta comment */}
                    <ReactMarkdown>{stripMetaBlock(data.bodyMarkdown)}</ReactMarkdown>
                </div>
            ) : data.description ? (
                // Description is authored as Markdown (form editor / Link); render it
                // the same way as the body so plain text and rich text both look right.
                <div className="prose prose-sm max-w-none text-gray-700 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-gray-900">
                    <ReactMarkdown>{data.description}</ReactMarkdown>
                </div>
            ) : null}

            {/* Competencies */}
            {data.competencies.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Kompetenser</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {data.competencies.map((c, i) => (
                            <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#A4C3B2]/30 text-[#1A4D4F] font-medium">{c}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* Extra sections (course gating, onboarding, ...) */}
            {children}

            {/* Contacts */}
            {data.contacts.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Kontakt</h3>
                    <div className="space-y-2">
                        {data.contacts.map((c, i) => (
                            <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 p-3 rounded-xl border border-gray-100 bg-white text-sm">
                                <span className="font-medium text-gray-900">{c.name}</span>
                                {c.role && <span className="text-gray-500">{c.role}</span>}
                                {c.email && <a href={`mailto:${c.email}`} className="text-[#1A4D4F] hover:underline flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{c.email}</a>}
                                {c.phone && <a href={`tel:${c.phone}`} className="text-[#1A4D4F] hover:underline flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{c.phone}</a>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {footer}
        </div>
    );
};

// Strip the leading `<!-- meta: ... -->` block so it never renders as text.
const stripMetaBlock = (md: string): string => md.replace(/<!--\s*meta:[\s\S]*?-->/i, '').trim();

export default NeedView;
